import React, { memo } from 'react';

import cx from 'classnames';

import cls from './InventoryCard.module.css';
import { InventoryCardActions } from './InventoryCardActions';
import { InventoryImage } from './InventoryImage';
import { InventoryStatus } from '../InventoryStatus';
import { useInventoryStatus } from '../InventoryStatus/useInventoryStatus';

import type { EquipmentInventoryStatus } from '../../model';
import type { UiDropdownSearchListItem } from '@shared/ui';

declare const numberFormatter: {
    formatNumber: (value: number, precision?: number) => string;
};

export interface InventoryCardProps {
    guid: string;
    title: string;
    amount: number;
    status: EquipmentInventoryStatus;
    image?: string | null;
    freeDate?: string;
    isEditWarehouseMode?: boolean;
    isQuickEditInventoryMode?: boolean;
    warehouses?: UiDropdownSearchListItem[];
    hasFullAccess?: boolean;
    canMove?: boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onCopyClick?: React.MouseEventHandler<HTMLLIElement>;
    onDeleteClick?: React.MouseEventHandler<HTMLLIElement>;
    onMoveClick?: (warehouse: UiDropdownSearchListItem, closeDropdown: () => void) => void;
    onStatusClick?: (
        status: EquipmentInventoryStatus
    ) => React.MouseEventHandler<HTMLLIElement> | undefined;
}

export const InventoryCard = memo(
    ({
        guid,
        title,
        amount,
        status,

        image,
        freeDate,
        isEditWarehouseMode = false,
        isQuickEditInventoryMode = false,
        warehouses = [],
        hasFullAccess = true,
        canMove = true,
        onClick,
        onCopyClick,
        onDeleteClick,
        onMoveClick,
        onStatusClick,
    }: InventoryCardProps) => {
        const { inventoryData } = useInventoryStatus();

        return (
            <div
                className={cls.inventoryBlock}
                data-status={status}
                data-guid={guid}
                onClick={onClick}
            >
                <div
                    className={cls.inventoryWrapper}
                    style={{ backgroundColor: inventoryData[status].color }}
                >
                    <div className={cls.inventory}>
                        <InventoryImage image={image} />
                        <div className={cls.inventoryDataWrapper}>
                            <div className={cls.inventoryDescription}>
                                <h3
                                    className={cx(cls.inventoryTitle, {
                                        [cls.decommissionedTitle]: status === 3,
                                    })}
                                >
                                    {title}
                                </h3>
                            </div>
                            <div className={cls.footer}>
                                <InventoryStatus freeDate={freeDate} status={status} />
                                <span className={cls.amount}>
                                    <span
                                        className={cls.ellipsisAmount}
                                        title={numberFormatter.formatNumber(amount, 3)}
                                    >
                                        {numberFormatter.formatNumber(amount, 3)}
                                    </span>
                                </span>
                            </div>
                        </div>
                        {!isEditWarehouseMode && !isQuickEditInventoryMode && (
                            <InventoryCardActions
                                status={status}
                                warehouses={warehouses}
                                hasFullAccess={hasFullAccess}
                                canMove={canMove}
                                onCopyClick={onCopyClick}
                                onDeleteClick={onDeleteClick}
                                onMoveClick={onMoveClick}
                                onStatusClick={onStatusClick}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }
);
